import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const F = "'DM Sans', system-ui, sans-serif";

const ORG_TYPES = ["Hospital", "Inpatient Treatment Center", "Shelter", "Court/Probation", "DSS (Social Services)", "Behavioral Health Provider", "Community Organization", "Other"];
const MET_WITH = ["Front Desk", "Staff Member", "Supervisor", "Director/Administrator", "No one available"];
const ACTIONS = ["Meeting Scheduled", "Call Back Needed", "Drop-in Only", "Workshop Requested", "Not a Fit"];
const NEEDS = ["Housing", "IOP/PHP Programs", "Mental Health Support", "Peer Support", "Case Management", "Other"];

function Modal({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", padding: "1.5rem", maxWidth: "600px", width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

export default function OutreachTracker() {
  const { user, api } = useAuth();
  const [visits, setVisits] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState("form"); // form, visits, reports
  const [modal, setModal] = useState(null);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [visitsData, reportsData] = await Promise.all([
        api.get("/outreach/visits"),
        api.get("/outreach/reports"),
      ]);
      setVisits(visitsData);
      setReports(reportsData);
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmitVisit = async (formData) => {
    try {
      const newVisit = await api.post("/outreach/visits", formData);
      setVisits(prev => [newVisit, ...prev]);
      // Refresh reports
      const reportsData = await api.get("/outreach/reports");
      setReports(reportsData);
      notify("Visit logged");
      setTab("visits");
    } catch (err) { notify("Error: " + err.message); }
  };

  const handleDeleteVisit = async (visitId) => {
    if (!confirm("Delete this visit?")) return;
    try {
      await api.delete("/outreach/visits/" + visitId);
      setVisits(prev => prev.filter(v => v.id !== visitId));
      notify("Visit deleted");
    } catch (err) { notify("Error: " + err.message); }
  };

  const inp = { width: "100%", padding: "0.5rem 0.75rem", border: "1.5px solid #d1d5db", borderRadius: "6px", fontSize: "0.875rem", fontFamily: F, outline: "none", boxSizing: "border-box" };
  const lab = { display: "block", fontSize: "0.8125rem", fontWeight: "500", color: "#374151", marginBottom: "0.25rem", fontFamily: F };
  const card = { background: "#fff", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" };
  const tabBtn = (active) => ({ background: active ? "#059669" : "#e2e8f0", color: active ? "#fff" : "#334155", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: "600", cursor: "pointer", fontFamily: F });

  if (loading) return <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ fontFamily: F }}>Loading Outreach Tracker...</div></div>;

  return (
    <div style={{ minHeight: "calc(100vh - 50px)", background: "#f1f5f9" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "1.25rem" }}>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem" }}>
          <button onClick={() => setTab("form")} style={tabBtn(tab === "form")}>Log Visit</button>
          <button onClick={() => setTab("visits")} style={tabBtn(tab === "visits")}>All Visits ({visits.length})</button>
          <button onClick={() => setTab("reports")} style={tabBtn(tab === "reports")}>Reports</button>
        </div>

        {/* LOG VISIT FORM */}
        {tab === "form" && <VisitForm user={user} onSubmit={handleSubmitVisit} inp={inp} lab={lab} card={card} />}

        {/* ALL VISITS */}
        {tab === "visits" && (
          <div>
            {visits.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8", fontFamily: F }}>No visits logged yet.</div>
            ) : visits.map(v => (
              <div key={v.id} style={{ ...card, padding: "1rem", marginBottom: "0.75rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F }}>{v.organization_name}</div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F }}>{v.organization_type} | {new Date(v.visit_date).toLocaleDateString()}</div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F }}>Staff: {v.staff_name || "Unknown"} | Contact: {v.contact_name || "N/A"}</div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    {v.interest_level && (
                      <span style={{ background: v.interest_level === "hot" ? "#ef4444" : v.interest_level === "warm" ? "#f59e0b" : "#94a3b8", color: "#fff", fontSize: "0.5625rem", padding: "2px 6px", borderRadius: "10px", fontWeight: "600", fontFamily: F, textTransform: "uppercase" }}>{v.interest_level}</span>
                    )}
                    <button onClick={() => handleDeleteVisit(v.id)} style={{ background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: "4px", padding: "0.25rem 0.5rem", fontSize: "0.625rem", cursor: "pointer", fontFamily: F }}>Delete</button>
                  </div>
                </div>
                {v.notes && <div style={{ fontSize: "0.75rem", color: "#475569", fontFamily: F, marginTop: "0.375rem" }}>{v.notes}</div>}
                {v.needs_mentioned && v.needs_mentioned.length > 0 && (
                  <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginTop: "0.375rem" }}>
                    {v.needs_mentioned.map((n, i) => <span key={i} style={{ background: "#d1fae5", color: "#059669", fontSize: "0.5625rem", padding: "2px 6px", borderRadius: "10px", fontFamily: F }}>{n}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* REPORTS */}
        {tab === "reports" && reports && (
          <div>
            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.625rem", marginBottom: "1rem" }}>
              <div style={{ ...card, padding: "0.875rem" }}><div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F }}>Total Visits</div><div style={{ fontSize: "1.625rem", fontWeight: "700", color: "#0f172a", fontFamily: F }}>{reports.total_visits}</div></div>
              <div style={{ ...card, padding: "0.875rem" }}><div style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: F }}>Unique Orgs</div><div style={{ fontSize: "1.625rem", fontWeight: "700", color: "#059669", fontFamily: F }}>{reports.by_staff.reduce((a, s) => a + parseInt(s.unique_orgs), 0)}</div></div>
            </div>

            {/* Staff productivity */}
            <div style={{ ...card, padding: "1rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.5rem" }}>Staff Productivity</h3>
              {reports.by_staff.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ fontSize: "0.8125rem", fontFamily: F }}>{s.staff_name}</span>
                  <span style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{s.visit_count} visits, {s.unique_orgs} orgs</span>
                </div>
              ))}
            </div>

            {/* Interest breakdown */}
            <div style={{ ...card, padding: "1rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.5rem" }}>Interest Levels</h3>
              {reports.by_interest.map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0" }}>
                  <span style={{ fontSize: "0.8125rem", fontFamily: F, textTransform: "capitalize" }}>{item.interest_level}</span>
                  <span style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{item.count}</span>
                </div>
              ))}
            </div>

            {/* Needs analysis */}
            <div style={{ ...card, padding: "1rem" }}>
              <h3 style={{ fontSize: "0.875rem", fontWeight: "700", fontFamily: F, marginBottom: "0.5rem" }}>Needs Mentioned</h3>
              {reports.by_needs.map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.375rem 0" }}>
                  <span style={{ fontSize: "0.8125rem", fontFamily: F }}>{item.need}</span>
                  <span style={{ fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {toast && <div style={{ position: "fixed", bottom: "1.25rem", right: "1.25rem", background: "#22c55e", color: "#fff", padding: "0.625rem 1rem", borderRadius: "8px", boxShadow: "0 8px 20px rgba(0,0,0,0.2)", zIndex: 400, fontSize: "0.8125rem", fontWeight: "600", fontFamily: F }}>{toast}</div>}
    </div>
  );
}

// ===== VISIT FORM =====
function VisitForm({ user, onSubmit, inp, lab, card }) {
  const [form, setForm] = useState({
    organization_name: "", address: "", organization_type: "",
    contact_name: "", contact_title: "", contact_phone: "", contact_email: "",
    decision_maker: "", met_with: "", interest_level: "",
    action_taken: "", needs_mentioned: [], notes: "",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleNeed = (need) => setForm(p => ({
    ...p, needs_mentioned: p.needs_mentioned.includes(need) ? p.needs_mentioned.filter(n => n !== need) : [...p.needs_mentioned, need],
  }));

  const handleSubmit = () => {
    if (!form.organization_name) return;
    onSubmit(form);
    setForm({ organization_name: "", address: "", organization_type: "", contact_name: "", contact_title: "", contact_phone: "", contact_email: "", decision_maker: "", met_with: "", interest_level: "", action_taken: "", needs_mentioned: [], notes: "" });
  };

  return (
    <div style={{ ...card, padding: "1.5rem" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: "700", fontFamily: F, marginBottom: "0.75rem" }}>Log Outreach Visit</h3>
      <div style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: F, marginBottom: "1rem" }}>Staff: {user.name}</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <div><label style={lab}>Organization Name *</label><input value={form.organization_name} onChange={e => set("organization_name", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Organization Type</label><select value={form.organization_type} onChange={e => set("organization_type", e.target.value)} style={inp}><option value="">Select...</option>{ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
        <div style={{ gridColumn: "span 2" }}><label style={lab}>Address</label><input value={form.address} onChange={e => set("address", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Contact Name</label><input value={form.contact_name} onChange={e => set("contact_name", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Contact Title</label><input value={form.contact_title} onChange={e => set("contact_title", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Contact Phone</label><input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Contact Email</label><input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} style={inp} /></div>
        <div><label style={lab}>Met With</label><select value={form.met_with} onChange={e => set("met_with", e.target.value)} style={inp}><option value="">Select...</option>{MET_WITH.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
        <div><label style={lab}>Decision Maker?</label><div style={{ display: "flex", gap: "0.75rem", marginTop: "0.25rem" }}>{["Yes", "No", "Referred"].map(v => <label key={v} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8125rem", fontFamily: F, cursor: "pointer" }}><input type="radio" name="dm" checked={form.decision_maker === v} onChange={() => set("decision_maker", v)} />{v}</label>)}</div></div>
        <div><label style={lab}>Interest Level</label><div style={{ display: "flex", gap: "0.75rem", marginTop: "0.25rem" }}>{["hot", "warm", "cold"].map(v => <label key={v} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8125rem", fontFamily: F, cursor: "pointer", textTransform: "capitalize" }}><input type="radio" name="interest" checked={form.interest_level === v} onChange={() => set("interest_level", v)} />{v}</label>)}</div></div>
        <div><label style={lab}>Action Taken</label><div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.25rem" }}>{ACTIONS.map(a => <label key={a} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", fontFamily: F, cursor: "pointer" }}><input type="radio" name="action" checked={form.action_taken === a} onChange={() => set("action_taken", a)} />{a}</label>)}</div></div>
        <div style={{ gridColumn: "span 2" }}><label style={lab}>Needs Mentioned</label><div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.25rem" }}>{NEEDS.map(n => <label key={n} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", fontFamily: F, cursor: "pointer" }}><input type="checkbox" checked={form.needs_mentioned.includes(n)} onChange={() => toggleNeed(n)} />{n}</label>)}</div></div>
        <div style={{ gridColumn: "span 2" }}><label style={lab}>Notes</label><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} style={{ ...inp, resize: "vertical" }} /></div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button onClick={handleSubmit} style={{ background: "#059669", color: "#fff", border: "none", borderRadius: "6px", padding: "0.625rem 1.25rem", fontSize: "0.875rem", fontWeight: "600", cursor: "pointer", fontFamily: F }}>Submit Visit</button>
      </div>
    </div>
  );
}
